import React, { useState, useEffect } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import i18n from "i18next";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DayOfWeek } from "@fluentui/react-calendar-compat";

import { fetchWrapper } from "../../../helpers/fetchwrapper";
import { baseUrl } from "../../../helpers/constants";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  MessageBar,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogBody,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Label,
  Tab,
  TabList,
} from "@fluentui/react-components";
import {
  BoxSearchRegular,
  CalendarRegular,
  DismissRegular,
  FilterRegular,
  SearchRegular,
  SendRegular,
} from "@fluentui/react-icons";
import LuebPagedDataGrid from "../../../Controls/LuebPagedDataGrid";
import { IUserBedarfPosition } from "../../../Models/IUserBedarfPosition";
import { RequestsColumns } from "./RequestsColumns";
import Header from "../../Layout/Header";

interface IRequestsProps {}

function Requests(props: IRequestsProps) {
  const emptyList: any[] = [];
  const [
    offerSentIsOpen,
    { setTrue: showOfferSentDialog, setFalse: hideOfferSentDialog },
  ] = useBoolean(false);
  const [offerId, setOfferId] = useState("");
  const [offerError, setOfferError] = useState("");
  const [selectedItems, setSelectedItems] = useState(emptyList);

  const [resetCheckedItems, setReserCheckedItems] = useState<boolean>(false);

  const [checkedItems, setCheckedItems] = useState<IUserBedarfPosition[]>();
  const [selectedTabValue, setSelectedTabValue] = useState<string>("open");
  const [hoveredItem, setHoveredItem] = useState<string>();
  const [openRequests, setOpenRequests] = useState<IUserBedarfPosition[]>([]);
  const [finishedRequests, setFinishedRequests] = useState([]);

  const [userNameFilter, setUserNameFilter] = useState(
    window.location.search.substring(
      window.location.search.indexOf("&userNameFilter=") + 16,
    ),
  );
  const [startDateFilter, setStartDateFilter] = useState<Date | undefined>(
    undefined,
  );
  const [endDateFilter, setEndDateFilter] = useState<Date | undefined>(
    undefined,
  );

  const sentAQuote = () => {
    if (offerId) {
      const items: any[] = [];
      if (checkedItems) {
        checkedItems.forEach((item: any) => {
          items.push({ UserObjectGuid: item.userBedarfGuid, OfferId: offerId });
        });

        fetchWrapper
          .postWithAuth(`${baseUrl}/api/vertrieb/Finish`, items)
          .then((data: any) => {
            setOfferError("");
            hideOfferSentDialog();
            getOpenRequests();
            getFinishedRequests();
          })
          .catch((error) => {
            setOfferError(error);
          });
      }
    }
  };

  const getOpenRequests = () => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/vertrieb/GetRequests?filterOptions=open&userNameFilter=${userNameFilter}&startDateFilter=${
          startDateFilter ? startDateFilter.toUTCString() : ""
        }&endDateFilter=${endDateFilter ? endDateFilter.toUTCString() : ""}`,
      )
      .then((data: any) => {
        if (data && data.positionen) {
          setOpenRequests(data.positionen);
        }
      });
  };

  const getFinishedRequests = () => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/vertrieb/GetRequests?filterOptions=processed&userNameFilter=${userNameFilter}&startDateFilter=${
          startDateFilter ? startDateFilter.toUTCString() : ""
        }&endDateFilter=${endDateFilter ? endDateFilter.toUTCString() : ""}`,
      )
      .then((data: any) => {
        if (data && data.positionen) {
          setFinishedRequests(data.positionen);
        }
      });
  };

  const onFormatDate = (date?: Date): string => {
    return !date
      ? ""
      : date.getDate() + "." + (date.getMonth() + 1) + "." + date.getFullYear();
  };

  useEffect(() => {
    getOpenRequests();
    getFinishedRequests();
  }, []);
  useEffect(() => {
    if (resetCheckedItems == true) {
      setReserCheckedItems(false);
    }
  }, [resetCheckedItems]);

  return (
    <div>
      <Dialog open={offerSentIsOpen}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.t<string>("offerSent")}</DialogTitle>
            <DialogContent>
              <Label>{i18n.t<string>("offerSentText")}</Label>
              <Field label={i18n.t<string>("offerId")}>
                <Input
                  onChange={(ev, data) => {
                    setOfferId(data.value);
                  }}
                  value={offerId}
                ></Input>
                {offerError && (
                  <MessageBar intent="error">{offerError}</MessageBar>
                )}
              </Field>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                icon={<SendRegular />}
                onClick={() => {
                  sentAQuote();
                  setOfferId("");
                  setReserCheckedItems(true);
                }}
              >
                {i18n.t<string>("save")}
              </Button>
              <Button onClick={hideOfferSentDialog}>
                {i18n.t<string>("cancel")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Header
        HTMLElement={
          <StackShim horizontal verticalAlign="center">
            <Button
              icon={<BoxSearchRegular fontSize={24} color="#ff6600" />}
              size="large"
              appearance="subtle"
            >
              {i18n.t<string>("processRequests").toLocaleUpperCase()}
            </Button>
          </StackShim>
        }
      />

      <div className="lueb-InnerContentBlockPadding2px">
        <Button
          icon={<SearchRegular />}
          appearance="transparent"
          onClick={() => {
            getOpenRequests();
            getFinishedRequests();
          }}
        >
          {i18n.t<string>("suchen")}
        </Button>

        {selectedTabValue == "open" && (
          <Button
            appearance="transparent"
            icon={<SendRegular />}
            onClick={() => {
              showOfferSentDialog();
            }}
            disabled={checkedItems == undefined || checkedItems.length == 0}
          >
            {i18n.t<string>("enterOfferId")}
          </Button>
        )}
      </div>
      <div className="lueb-ContentBlock">
        <StackShim horizontal tokens={{ childrenGap: 5 }} wrap={true}>
          <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
            <Field label="Gesendet am (von)">
              <DatePicker
                contentAfter={
                  startDateFilter ? (
                    <Button
                      appearance="transparent"
                      icon={<DismissRegular />}
                      onClick={() => {
                        setStartDateFilter(undefined);
                      }}
                    />
                  ) : (
                    <CalendarRegular />
                  )
                }
                value={startDateFilter}
                firstDayOfWeek={DayOfWeek.Monday}
                onSelectDate={(ev: any) => {
                  setStartDateFilter(ev);
                  if (endDateFilter != undefined) {
                    if (ev > endDateFilter!) {
                      setEndDateFilter(ev);
                    }
                  }
                }}
                formatDate={onFormatDate}
                maxDate={new Date()}
                placeholder="von"
              />
            </Field>
          </StackItemShim>
          <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
            <Field label="Gesendet am (bis)">
              <DatePicker
                contentAfter={
                  endDateFilter ? (
                    <Button
                      appearance="transparent"
                      icon={<DismissRegular />}
                      onClick={() => {
                        setEndDateFilter(undefined);
                      }}
                    />
                  ) : (
                    <CalendarRegular />
                  )
                }
                value={endDateFilter}
                firstDayOfWeek={DayOfWeek.Monday}
                onSelectDate={(ev: any) => {
                  setEndDateFilter(ev);
                }}
                placeholder="bis"
                minDate={startDateFilter}
                maxDate={new Date()}
                formatDate={onFormatDate}
              />
            </Field>
          </StackItemShim>
          <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
            <Field label="Gesendet von (E-Mail)">
              <Input
                placeholder="Email-Adresse"
                value={userNameFilter}
                contentAfter={
                  userNameFilter ? (
                    <Button
                      appearance="transparent"
                      icon={<DismissRegular />}
                      onClick={() => {
                        setUserNameFilter("");
                      }}
                    />
                  ) : (
                    <FilterRegular />
                  )
                }
                onChange={(ev, data) => {
                  setUserNameFilter(data ? data.value : "");
                }}
              />
            </Field>
          </StackItemShim>
        </StackShim>
      </div>
      <TabList
        selectedValue={selectedTabValue}
        onTabSelect={(ev, data) => {
          if (typeof data.value == "string") {
            setSelectedTabValue(data.value);
          }
        }}
      >
        <Tab value={"open"}>{i18n.t<string>("open")}</Tab>
        <Tab value={"finished"}>{i18n.t<string>("finished")}</Tab>
      </TabList>
      <div className="lueb-ContentBlock">
        <LuebPagedDataGrid
          sortable
          columns={RequestsColumns({
            checkedItemsGuid: (item: IUserBedarfPosition[]) => {
              setCheckedItems(item);
            },
            resetChecked: resetCheckedItems,
            selectedTab: selectedTabValue,
            allItems:
              selectedTabValue == "open" ? openRequests : finishedRequests,
            hoveredItem: hoveredItem,
          })}
          /*           onItemHover={(item: IUserBedarfPosition) => {
            if (item != undefined) {
              setHoveredItem(item.userBedarfGuid);
            } else setHoveredItem(undefined);
          }} */
          items={selectedTabValue == "open" ? openRequests : finishedRequests}
          resizableColumns={true}
          columnSizingOptions={{
            checkboxId: { minWidth: 50, defaultWidth: 50 },
            artikelId: { minWidth: 70, defaultWidth: 100 },
            bennenungEN: { minWidth: 110, defaultWidth: 110 },
            bennenungDE: { minWidth: 110, defaultWidth: 110 },
            menge: { minWidth: 50, defaultWidth: 50 },
            "createdBy.email": { minWidth: 200, defaultWidth: 260 },
            gesendetAm: { minWidth: 80, defaultWidth: 80 },
            "bearbeitetVon.email": { minWidth: 170, defaultWidth: 170 },
            bearbeitetAm: { minWidth: 80, defaultWidth: 80 },
            "model?.offerId": { minWidth: 80, defaultWidth: 80 },
          }}
        />
      </div>
    </div>
  );
}

export default Requests;
